import {
  useGetCategoryPathQuery,
  useGetConversationIdByListingIdQuery,
  useGetInitialStructuredMessageQuery,
} from '@kijiji/generated/graphql-types'
import { isPositiveInteger } from '@kijiji/number/isPositiveInteger'
import { useSession } from 'next-auth/react'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { isUserAuthenticated } from '@/features/auth/constants/user'
import { R2SExistingConversation } from '@/features/listing/components/reply-to-seller/conversations/R2SExistingConversation'
import {
  type R2SFormProps,
  R2SForm,
} from '@/features/listing/components/reply-to-seller/conversations/R2SForm'
import { getPrepopulatedMessage } from '@/features/listing/utils/reply-to-seller/getPrepopulatedMessage'
import { useLocale } from '@/hooks/useLocale'
import { Flex } from '@/ui/atoms/flex'
import { Skeleton } from '@/ui/atoms/skeleton'

export type R2SProps = Omit<R2SFormProps, 'structuredMessages'> & {
  /**
   * Category ID of the listing.
   */
  categoryId: number

  /**
   * Specifies if the R2S form should be disabled
   */
  disableForm?: boolean

  /**
   * Location ID of the listing.
   */
  locationId: number
  /**
   * Callback to be passed down to the R2SForm component
   */
  callbackForR2S?: () => void
}

/**
 * Wrapper for R2S. Encapsulates the R2S message form.
 *
 * @see {@link R2SFormProps} for the rest of the props that can be passed.
 */
export const R2S: FC<R2SProps> = ({
  categoryId,
  disableForm,
  locationId,
  listingId,
  callbackForR2S,
  ...r2sFormProps
}) => {
  const { cookieLocale, apiLocale } = useLocale()
  const { status } = useSession()
  const { spacing } = useTheme()

  // Only fetch if they're logged in and the ad is not CAS / MOVE
  const shouldFetchConversationId =
    isUserAuthenticated(status) && isPositiveInteger(parseInt(listingId))

  const { data: existingConversation, loading: conversationLoading } =
    useGetConversationIdByListingIdQuery({
      skip: !shouldFetchConversationId,
      fetchPolicy: 'cache-first',
      variables: { adId: parseInt(listingId) },
    })

  const { data: { initialStructuredMessage } = {}, loading: structuredMessagesLoading } =
    useGetInitialStructuredMessageQuery({
      skip: !isUserAuthenticated(status),
      variables: { categoryId, locale: cookieLocale },
    })

  const { data: categoryData } = useGetCategoryPathQuery({
    variables: { categoryId, locationId, locale: cookieLocale },
    fetchPolicy: 'cache-first',
  })

  /**
   * It should show loading state if the user is authenticated
   * while we query if there is an existing conversation for this listing
   */
  if ((isUserAuthenticated(status) && conversationLoading) || structuredMessagesLoading) {
    return <Skeleton fitContainer variant="rectangle" height="15rem" />
  }

  /**
   * It should not show the form if there is an existing conversation for the listing
   */
  if (!conversationLoading && existingConversation?.conversationIdByAdId?.id) {
    return <R2SExistingConversation conversationId={existingConversation.conversationIdByAdId.id} />
  }

  /**
   * Should only try to fetch Structured messages if the form will appear
   * And IF there is no structured messages available
   * */
  const initialMessage =
    !initialStructuredMessage && !structuredMessagesLoading
      ? getPrepopulatedMessage(
          categoryId,
          apiLocale,
          categoryData?.category?.categoryPaths ?? undefined
        )
      : undefined

  /**
   * It should only show the form if the user is not authenticated
   * OR if there is no conversationID on page load
   */
  return (
    <Flex flexDirection="column" data-testid="r2s" gap={spacing.default}>
      <R2SForm
        {...r2sFormProps}
        disableForm={disableForm}
        categoryId={categoryId}
        initialMessage={initialMessage}
        listingId={listingId}
        structuredMessages={initialStructuredMessage}
        successCallbackFn={callbackForR2S}
      />
    </Flex>
  )
}
